import { QueryClient } from '@tanstack/react-query'
import {
  fetchAnalysis,
  fetchTickers,
  fetchQuantitative,
  fetchBacktestLeaderboard,
  fetchBacktestRecords,
  fetchBacktestStats,
  fetchFactorUniverse,
  fetchMLStats,
  fetchMLFactors,
  fetchStrategies,
  fetchStrategiesUniverse,
  fetchCrowdedTrades,
  fetchCreditDashboard,
  listMoatJobs,
  fetchCalibrationReport,
  fetchCalibrationHistory,
} from '@/lib/api'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 120_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// ---------------------------------------------------------------------------
// Route prefetch helper
// ---------------------------------------------------------------------------
// Call this on sidebar nav hover (80ms debounced) or ticker link mouseenter
// to warm the cache before the user navigates. Each route maps to the queries
// it will fire on mount so the data is ready when the page renders.
//
// Route format examples:
//   '/dashboard'            -> prefetch ticker list
//   '/analysis/AAPL'        -> prefetch analysis for AAPL
//   '/dashboard/AAPL'       -> prefetch quantitative data for AAPL
//   any other route         -> no-op (safe to call always)
// ---------------------------------------------------------------------------

const STALE_5M = 5 * 60_000

export function prefetchRouteData(
  route: string,
  _params?: Record<string, unknown>,
): void {
  // /dashboard and / -> prefetch ticker list
  if (route === '/dashboard' || route === '/') {
    queryClient.prefetchQuery({
      queryKey: ['tickers'],
      queryFn: () => fetchTickers(),
      staleTime: 30_000,
    })
    return
  }

  // /analysis/:ticker
  const analysisMatch = route.match(/^\/analysis\/([A-Z0-9.]{1,10})$/i)
  if (analysisMatch) {
    const ticker = analysisMatch[1].toUpperCase()
    queryClient.prefetchQuery({
      queryKey: ['analysis', ticker],
      queryFn: () => fetchAnalysis(ticker),
      staleTime: 60_000,
    })
    return
  }

  // /dashboard/:ticker (phase 5.2 dashboard)
  const dashboardTickerMatch = route.match(/^\/dashboard\/([A-Z0-9.]{1,10})/i)
  if (dashboardTickerMatch) {
    const ticker = dashboardTickerMatch[1].toUpperCase()
    queryClient.prefetchQuery({
      queryKey: ['phase52', 'quantitative', ticker],
      queryFn: () => fetchQuantitative(ticker),
      staleTime: STALE_5M,
    })
    return
  }

  // ── Intelligence routes ──────────────────────────────────────────────────
  // The 5 raw-useState pages share an SWR cache via cachedFetch — calling the
  // fetcher here warms that cache so the page mount renders synchronously.
  // The 4 react-query pages use queryClient.prefetchQuery to populate the
  // QueryClient cache they actually read from.
  const intelligencePrefetch: Record<string, () => void> = {
    '/backtesting': () => {
      void fetchBacktestRecords({ limit: 25, offset: 0 }).catch(() => {})
      void fetchBacktestStats().catch(() => {})
      void fetchBacktestLeaderboard().catch(() => {})
    },
    '/batch': () => {
      void fetchFactorUniverse({ limit: 500 }).catch(() => {})
    },
    '/factors': () => {
      void fetchFactorUniverse({ limit: 200 }).catch(() => {})
    },
    '/ml-insights': () => {
      void fetchMLStats().catch(() => {})
      void fetchMLFactors().catch(() => {})
    },
    '/strategies': () => {
      void fetchStrategiesUniverse().catch(() => {})
      void fetchStrategies().catch(() => {})
    },
    '/moat': () => {
      queryClient.prefetchQuery({
        queryKey: ['moat-jobs'],
        queryFn: () => listMoatJobs({ limit: 50 }),
        staleTime: STALE_5M,
      })
    },
    '/credit-universe': () => {
      queryClient.prefetchQuery({
        queryKey: ['credit-dashboard'],
        queryFn: fetchCreditDashboard,
        staleTime: STALE_5M,
      })
    },
    '/crowded-trades': () => {
      queryClient.prefetchQuery({
        queryKey: ['crowded-trades', 25],
        queryFn: () => fetchCrowdedTrades(25),
        staleTime: STALE_5M,
      })
    },
    '/calibration': () => {
      queryClient.prefetchQuery({
        queryKey: ['calibration', 'report'],
        queryFn: fetchCalibrationReport,
        staleTime: STALE_5M,
      })
      queryClient.prefetchQuery({
        queryKey: ['calibration', 'history'],
        queryFn: () => fetchCalibrationHistory(20),
        staleTime: STALE_5M,
      })
    },
  }
  if (intelligencePrefetch[route]) {
    intelligencePrefetch[route]()
    return
  }

  // All other routes: no-op (safe)
}
